import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, BookHeart, Download } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { IMAGES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PDF_URL = "/journaling-prompts.pdf";

export default function LeadMagnet() {
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) {
            toast.error("Please enter your email.");
            return;
        }
        try {
            setLoading(true);
            await axios.post(`${API}/subscribe`, {
                email: email.trim(),
                name: name.trim() || null,
                source: "homepage_lead_magnet",
            });
            setDone(true);
            // Auto-trigger download
            const a = document.createElement("a");
            a.href = PDF_URL;
            a.download = "soul-sync-healing-7-journaling-prompts.pdf";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            toast.success(
                "Welcome in. Your prompts are downloading — check your downloads folder.",
            );
        } catch (err) {
            toast.error(
                "Something went wrong. Please try again or message us via WhatsApp.",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            className="relative bg-[#FAF8F5] border-y border-[#EFEBE4]"
            data-testid="lead-magnet"
            id="free-prompts"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                    {/* Visual */}
                    <div className="lg:col-span-5 ssh-reveal">
                        <div className="relative">
                            <div className="aspect-[4/5] overflow-hidden">
                                <img
                                    src={IMAGES.abstractTexture}
                                    alt="Soft pages — journaling prompts"
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            </div>
                            <div className="absolute -bottom-6 -right-6 bg-white border border-[#EFEBE4] px-7 py-6 hidden md:block max-w-[260px]">
                                <BookHeart
                                    className="h-7 w-7 text-[#A68A6B]"
                                    strokeWidth={1.25}
                                />
                                <p className="mt-3 font-serif text-xl text-[#2C2B29] leading-tight">
                                    7 prompts. 7 quiet returns to yourself.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Form */}
                    <div className="lg:col-span-7 ssh-reveal">
                        <p className="ssh-eyebrow">Free · A gentle gift</p>
                        <h2 className="mt-5 font-serif font-light tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-5xl text-[#2C2B29]">
                            Seven gentle journaling prompts{" "}
                            <em className="italic text-[#A68A6B]">
                                for inner healing.
                            </em>
                        </h2>
                        <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#4A4947] max-w-xl">
                            A small, soft companion for the days you want to
                            sit with yourself without performing okayness.
                            Drop your email and we'll send the PDF — plus the
                            occasional note from Shreya, whenever there's
                            something worth saying.
                        </p>

                        {!done ? (
                            <form
                                onSubmit={onSubmit}
                                className="mt-10 max-w-xl"
                                data-testid="lead-magnet-form"
                            >
                                <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                                    <Input
                                        value={name}
                                        onChange={(e) =>
                                            setName(e.target.value)
                                        }
                                        placeholder="Your first name (optional)"
                                        className="sm:col-span-2 bg-white border-[#EFEBE4] rounded-none h-14 focus-visible:ring-[#8A9A86]"
                                        data-testid="lead-magnet-name"
                                    />
                                    <Input
                                        type="email"
                                        value={email}
                                        onChange={(e) =>
                                            setEmail(e.target.value)
                                        }
                                        placeholder="you@example.com"
                                        className="sm:col-span-3 bg-white border-[#EFEBE4] rounded-none h-14 focus-visible:ring-[#8A9A86]"
                                        data-testid="lead-magnet-email"
                                        required
                                    />
                                </div>
                                <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-5">
                                    <Button
                                        type="submit"
                                        disabled={loading}
                                        className="ssh-btn-primary rounded-none px-9 py-7 text-xs uppercase tracking-[0.22em] inline-flex items-center gap-3"
                                        data-testid="lead-magnet-submit"
                                    >
                                        {loading
                                            ? "Sending…"
                                            : "Send me the prompts"}
                                        <ArrowRight
                                            className="h-4 w-4"
                                            strokeWidth={1.5}
                                        />
                                    </Button>
                                    <p className="text-xs text-[#75736F] leading-relaxed max-w-xs">
                                        No spam. Unsubscribe whenever. Your
                                        email stays with us.
                                    </p>
                                </div>
                            </form>
                        ) : (
                            <div
                                className="mt-10 max-w-xl bg-white border border-[#EFEBE4] p-8"
                                data-testid="lead-magnet-success"
                            >
                                <p className="ssh-eyebrow">You're in.</p>
                                <h3 className="mt-3 font-serif text-2xl text-[#2C2B29]">
                                    Your prompts are downloading.
                                </h3>
                                <p className="mt-3 text-base text-[#4A4947] leading-relaxed">
                                    If the download didn't start, tap below.
                                </p>
                                <a
                                    href={PDF_URL}
                                    download
                                    className="mt-5 inline-flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-[#A68A6B] hover:text-[#897157] transition-colors"
                                    data-testid="lead-magnet-download-link"
                                >
                                    <Download
                                        className="h-4 w-4"
                                        strokeWidth={1.5}
                                    />
                                    Download the PDF
                                </a>
                                <p className="mt-6 pt-6 border-t border-[#EFEBE4] text-sm text-[#4A4947] leading-relaxed">
                                    Want to go deeper? Begin with a{" "}
                                    <strong>30-minute pre-talk with Shreya</strong>{" "}
                                    — a soft place to start.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
